import useToken from 'hooks/useToken';
import React, { useEffect, useState, useContext } from 'react';
import { Alert, Card, Col, Row, Badge as BootstrapBadge } from 'react-bootstrap';
import { FiCalendar, FiUser, FiFileText } from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import TeamVacationsCalendar from '../../profile/TeamVacationsCalendar';
import VacationDetailsModal from '../../profile/VacationDetailsModal';
import AppContext from 'context/Context';

const VacacionesPendientes = () => {
    const { token } = useToken();
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const { config: { isDark } } = useContext(AppContext);

    const getStatusBadge = (estado) => {
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' }
        };
        return estados[estado?.toUpperCase()] || { bg: 'secondary', text: estado };
    };

    const SolicitudSkeleton = () => (
        <Row className="g-2 p-2">
            {[1, 2, 3, 4].map((i) => (
                <Col key={i} xs={12}>
                    <div style={{ padding: "12px", border: "1px solid #e3e3e3", borderRadius: "8px", background: "transparent" }}>
                        <Skeleton height={20} width="70%" className="mb-2" />
                        <Skeleton height={16} width="100%" className="mb-2" />
                        <Skeleton height={16} width="90%" className="mb-2" />
                        <Skeleton height={14} width="40%" />
                    </div>
                </Col>
            ))}
        </Row>
    );

    const obtenerImagen = async (idUsuario) => {
        if (!idUsuario) return '/img/team/nofoto50.png';
        try {
            const response = await fetch(`/api/perfil/ObtenerImagenPerfil?idUsuario=${idUsuario}`, {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (!response.ok) return '/img/team/nofoto50.png';

            const base64String = await response.text();
            return base64String || '/img/team/nofoto50.png';
        } catch (err) {
            console.error("Error al traer la foto del usuario: ", idUsuario, err);
            return '/img/team/nofoto50.png';
        }
    };

    const fetchSolicitudes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/vacaciones/ObtenerVacacionesPendientes', {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (!response.ok) throw new Error('Error al obtener solicitudes');
            const data = await response.json();

            if (data.iResponseType === 1) {
                const parsed = JSON.parse(data.detalles);

                const cabeceras = parsed.table2 || parsed.Table2 || [];
                const detallesDias = parsed.table3 || parsed.Table3 || [];
                console.log("📋 [VacacionesPendientes] cabeceras recibidas:", cabeceras);

                const solicitudesConDias = cabeceras.map(solicitud => {
                    const diasAsignados = detallesDias
                        .filter(dia => dia.idVacacion === solicitud.idVacacion)
                        .map(dia => dia.dFechaVacaciones ? dia.dFechaVacaciones.split('T')[0] : '')
                        .filter(Boolean)
                        .sort((a, b) => new Date(a) - new Date(b));

                    return {
                        ...solicitud,
                        diasDesglosados: diasAsignados
                    };
                });

                const fotosPefil = await Promise.all(
                    solicitudesConDias.map(async (solicitud) => {
                        const foto = await obtenerImagen(solicitud.idUsuario);
                        return {
                            ...solicitud,
                            fotoEmpleado: foto
                        };
                    })
                );

                setSolicitudes(fotosPefil);
            } else {
                setSolicitudes([]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchSolicitudes();
        }
    }, [token]);

    return (
        <Card className="border-0 shadow-sm rounded-3">
            <Card.Header className="bg-light border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        <h5 className="mb-0 fw-bold text-800">Vacaciones Pendientes</h5>
                        {solicitudes.length > 0 && (
                            <span className="badge bg-primary bg-opacity-10 text-primary ms-2 rounded-pill py-1 px-2 small">
                                {solicitudes.length}
                            </span>
                        )}
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-2 overflow-x-hidden" id="scrollableTargetVacaciones" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {loading ? (
                    <SolicitudSkeleton />
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    solicitudes.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 className="text-muted mb-2">Sin solicitudes</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                No hay solicitudes de vacaciones pendientes
                            </p>
                        </div>
                    ) : (
                        <Row className="g-2">
                            {solicitudes.map((solicitud, index) => {
                                const diasDesglosados = solicitud.diasDesglosados || [];
                                const fechaBase = solicitud.dFechaVacaciones ? solicitud.dFechaVacaciones.split('T')[0] : '';
                                const fechaInicio = diasDesglosados.length > 0 ? diasDesglosados[0] : fechaBase;
                                const fechaFin = diasDesglosados.length > 0 ? diasDesglosados[diasDesglosados.length - 1] : fechaBase;

                                return (
                                    <Col key={`${solicitud.idVacacion}-${index}`} xs={12}>
                                        <div
                                            style={{
                                                padding: "16px 20px",
                                                border: "1px solid #e3e3e3",
                                                borderRadius: "12px",
                                                background: isDark ? "linear-gradient(135deg, #121e2d 0%, #162231 100%)" : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                                transition: "all 0.3s ease",
                                                cursor: "pointer",
                                                position: "relative",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                                borderLeft: `4px solid ${getStatusBadge(solicitud.estado).bg === 'warning' ? '#ffc107' :
                                                    getStatusBadge(solicitud.estado).bg === 'success' ? '#198754' :
                                                        getStatusBadge(solicitud.estado).bg === 'danger' ? '#dc3545' : '#6c757d'}`
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-2px)";
                                                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                                                e.currentTarget.style.borderColor = "#0d6efd";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
                                                e.currentTarget.style.borderColor = "#e3e3e3";
                                            }}
                                            onClick={() => {
                                                setSelectedEmployee({
                                                    ...solicitud,
                                                    name: solicitud.nombreEmpleado,
                                                    position: solicitud.vNombreTipoUsuario || solicitud.vPuesto || '',
                                                    vNombreTipoUsuario: solicitud.vNombreTipoUsuario || solicitud.vPuesto,
                                                    iCategoria: solicitud.iCategoria,
                                                    vacationStart: fechaInicio,
                                                    vacationEnd: fechaFin,
                                                    daysRequested: solicitud.iDiasSolicitados,
                                                    dCreatedDate: solicitud.dCreatedDate,
                                                    diasDesglosados: diasDesglosados
                                                });
                                                setShowModal(true);
                                            }}
                                        >
                                            <span style={{
                                                position: "absolute",
                                                top: "12px",
                                                right: "16px",
                                                fontSize: "0.7rem",
                                                padding: "2px 6px",
                                                background: getStatusBadge(solicitud.estado).bg === 'warning' ? '#ffc107' :
                                                    getStatusBadge(solicitud.estado).bg === 'success' ? '#198754' :
                                                        getStatusBadge(solicitud.estado).bg === 'danger' ? '#dc3545' : '#6c757d',
                                                borderRadius: "4px",
                                                color: getStatusBadge(solicitud.estado).bg === 'warning' ? '#000' : '#fff'
                                            }}>
                                                {getStatusBadge(solicitud.estado).text}
                                            </span>

                                            <div className="mb-3 d-flex align-items-center" style={{ paddingRight: "60px" }}>
                                                <img
                                                    src={solicitud.fotoEmpleado || '/img/team/nofoto50.png'}
                                                    alt={solicitud.nombreEmpleado}
                                                    style={{
                                                        width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover',
                                                        border: '2px solid #e9ecef', marginRight: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "/img/team/nofoto50.png";
                                                    }}
                                                />
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: "1rem", marginBottom: "4px" }}>
                                                        <FiUser size={16} className="text-primary me-2" />
                                                        <Link
                                                            to={`/perfil/${solicitud.idUsuario}`}
                                                            className="text-decoration-none text-dark"
                                                            style={{ fontSize: "1rem" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {solicitud.nombreEmpleado}
                                                        </Link>
                                                    </h6>
                                                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                        Solicitud #{solicitud.idVacacion} (Total: {solicitud.iDiasSolicitados} días)
                                                    </small>
                                                </div>
                                            </div>

                                            {fechaInicio && (
                                                <div className="mb-2">
                                                    <FiCalendar size={14} className="text-info me-2" />
                                                    <span className="fw-semibold text-secondary" style={{ fontSize: "0.85rem" }}>Periodo solicitado: </span>
                                                    <span className="text-dark fw-medium" style={{ fontSize: "0.85rem" }}>
                                                        {new Date(fechaInicio + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(fechaFin + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="mb-2 d-flex align-items-start">
                                                <FiFileText size={14} className="text-muted me-2 mt-1 flex-shrink-0" />
                                                <div style={{ fontSize: "0.85rem" }}>
                                                    <span className="fw-semibold text-secondary">Motivo: </span>
                                                    {solicitud.vMotivoSolicitud && solicitud.vMotivoSolicitud.trim() !== '' ? (
                                                        <span className="text-dark bg-light px-2 py-0.5 rounded border text-800" style={{ fontStyle: 'italic', display: 'inline-block' }}>
                                                            "{solicitud.vMotivoSolicitud}"
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted small style-italic" style={{ fontStyle: 'italic' }}>No especificado</span>
                                                    )}
                                                </div>
                                            </div>

                                            {diasDesglosados.length > 0 && (
                                                <div className="mt-2 p-2 bg-white rounded border">
                                                    <span className="fw-semibold text-secondary" style={{ fontSize: "0.8rem" }}>
                                                        Días solicitados desglosados ({diasDesglosados.length}):
                                                    </span>
                                                    <div className="mt-1">
                                                        <small className="text-muted" style={{ lineHeight: '1.4' }}>
                                                            {diasDesglosados.map((fecha, idx) => (
                                                                <BootstrapBadge key={idx} bg="light" text="dark" className="me-1 mb-1 border" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                                                                    {new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                </BootstrapBadge>
                                                            ))}
                                                        </small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    )
                )}
            </Card.Body>
            <Card.Footer className="bg-light border-top">
                <div className="text-center text-muted small fw-medium">
                    {solicitudes.length} solicitud{solicitudes.length !== 1 ? 'es' : ''} pendiente{solicitudes.length !== 1 ? 's' : ''}
                </div>
            </Card.Footer>
            <div className="p-4 bg-transparent border-top">
                <TeamVacationsCalendar soloCalendario={true} />
            </div>
            <VacationDetailsModal
                show={showModal}
                onHide={() => setShowModal(false)}
                employee={selectedEmployee}
                onSuccess={fetchSolicitudes}
                showActions={true}
            />
        </Card>
    );
};

export default VacacionesPendientes;